import { useMemo, useState, useLayoutEffect, useRef } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

// Environment flag to show/hide analysis panel (defaults to false for production)
const SHOW_ANALYSIS_PANEL = import.meta.env.VITE_SHOW_ANALYSIS_PANEL === "true";

import styles from "./Answer.module.css";
import alertStyles from "../AlertBox/AlertBox.module.css";

import { RAGChatCompletion } from "../../api/models";
import { parseAnswerToHtml, AlertMarker, AlertLevel } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

// Helper function to create alert box DOM element (same pattern as vehicle cards)
function createAlertElement(marker: AlertMarker): HTMLDivElement {
    const levelConfig: Record<AlertLevel, { icon: string; className: string }> = {
        critical: { icon: "🚨", className: "critical" },
        medium: { icon: "⚠️", className: "medium" },
        high: { icon: "✅", className: "high" }
    };

    const config = levelConfig[marker.level];
    const isCritical = marker.level === "critical";
    const alertBox = document.createElement('div');

    // Apply criticalBox class for enhanced styling on critical alerts
    alertBox.className = `${alertStyles.alertBox} ${alertStyles[config.className]}${isCritical ? ` ${alertStyles.criticalBox}` : ''}`;

    // Vehicle context header for critical alerts
    if (isCritical && marker.vehicleName) {
        const vehicleContext = document.createElement('div');
        vehicleContext.className = alertStyles.vehicleContext;
        let vehicleHtml = `<span class="${alertStyles.vehicleName}">${marker.vehicleName}</span>`;
        if (marker.vehiclePrice) {
            vehicleHtml += `<span class="${alertStyles.vehiclePrice}"> · ${marker.vehiclePrice}</span>`;
        }
        vehicleContext.innerHTML = vehicleHtml;
        alertBox.appendChild(vehicleContext);
    }

    // Header
    const header = document.createElement('div');
    header.className = alertStyles.alertHeader;
    header.innerHTML = `<span class="${alertStyles.alertIcon}">${config.icon}</span><span class="${alertStyles.alertTitle}">${marker.title}</span>`;
    alertBox.appendChild(header);

    // Items list
    const list = document.createElement('ul');
    list.className = alertStyles.alertList;
    marker.items.forEach(item => {
        const li = document.createElement('li');
        li.className = alertStyles.alertItem;
        if (item.icon) {
            li.innerHTML = `<span class="${alertStyles.itemIcon}">${item.icon}</span><span class="${alertStyles.itemText}">${item.text}</span>`;
        } else {
            li.innerHTML = `<span class="${alertStyles.itemText}">${item.text}</span>`;
        }
        list.appendChild(li);
    });
    alertBox.appendChild(list);

    // Recommendation
    if (marker.recommendation) {
        const rec = document.createElement('div');
        rec.className = alertStyles.alertRecommendation;
        rec.innerHTML = `<span class="${alertStyles.recommendationIcon}">👉</span><span class="${alertStyles.recommendationText}">${marker.recommendation}</span>`;
        alertBox.appendChild(rec);
    }

    return alertBox;
}

interface Props {
    answer: RAGChatCompletion;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    onCarClick?: (car: any) => void;
    onShowPanelClick?: () => void;
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    onCarClick,
    onShowPanelClick
}: Props) => {
    const [isReferencesCollapsed, setIsReferencesCollapsed] = useState(true);
    const [renderedCards, setRenderedCards] = useState<Map<number, HTMLDivElement>>(new Map());
    const [renderedAlerts, setRenderedAlerts] = useState<Map<number, HTMLDivElement>>(new Map());
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
    const injectedPositionsRef = useRef<Set<number>>(new Set());
    const answerTextRef = useRef<HTMLDivElement>(null);
    const followupQuestions = answer.context.followup_questions;
    const messageContent = answer.message.content;
    const dataPoints = answer.context.data_points;

    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked, dataPoints), [messageContent, isStreaming, onCitationClicked, dataPoints]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    // ROOT CAUSE FIX: Inject persistent vehicle card elements into placeholders BEFORE browser paint
    // useLayoutEffect runs synchronously after DOM mutations but BEFORE browser paint
    // This eliminates the flicker caused by useEffect's post-paint timing
    useLayoutEffect(() => {
        if (!answerTextRef.current) return;

        parsedAnswer.imageMarkers.forEach((marker) => {
            const placeholder = answerTextRef.current?.querySelector(`[data-image-index="${marker.position}"]`) as HTMLElement;
            if (!placeholder) return;

            // Skip if this placeholder already has the card
            if (placeholder.hasChildNodes()) {
                return;
            }

            // Check if we already have a rendered card for this position
            let card = renderedCards.get(marker.position);

            if (!card) {
                // First time: Create new card element with image and vehicle info
                card = document.createElement('div');
                card.className = 'vehicle-card';

                // Make card clickable if onCarClick is provided
                if (onCarClick) {
                    card.style.cursor = 'pointer';
                    card.onclick = () => {
                        const carData = dataPoints[marker.carId];
                        if (carData) {
                            onCarClick(carData);
                        }
                    };
                }

                // Create image container
                const imageContainer = document.createElement('div');
                imageContainer.className = 'vehicle-card-image-container';

                // Add price indicator badge if price data available
                if (marker.vehicleData?.precio && marker.vehicleData?.precio_mercado_promedio && marker.vehicleData?.precio_mercado_maximo) {
                    const priceIndicator = document.createElement('div');
                    priceIndicator.className = 'vehicle-card-price-badge';

                    // Determine price status using same logic as PriceIndicator component
                    let status, label, color;
                    if (marker.vehicleData.precio <= marker.vehicleData.precio_mercado_promedio) {
                        status = 'good';
                        label = 'Buen Precio';
                        color = '#22c55e';
                    } else if (marker.vehicleData.precio <= marker.vehicleData.precio_mercado_maximo) {
                        status = 'fair';
                        label = 'Precio Justo';
                        color = '#ffa500';
                    } else {
                        status = 'high';
                        label = 'Revisa Precio';
                        color = '#ffa500';
                    }

                    // Create icon based on status
                    let iconSvg;
                    if (status === 'good' || status === 'fair') {
                        // Checkmark icon
                        iconSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="${color}" />
                            <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>`;
                    } else {
                        // Exclamation mark
                        iconSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="${color}" />
                            <line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="12" cy="16" r="1" fill="white" />
                        </svg>`;
                    }

                    priceIndicator.innerHTML = `${iconSvg}<span style="color: ${color}; margin-left: 4px;">${label}</span>`;
                    imageContainer.appendChild(priceIndicator);
                }

                const img = document.createElement('img');
                img.src = marker.imageUrl;
                img.alt = marker.altText;
                img.className = 'vehicle-card-image';
                img.loading = 'eager';

                imageContainer.appendChild(img);
                card.appendChild(imageContainer);

                // Create info container only if we have vehicle data
                if (marker.vehicleData) {
                    const infoContainer = document.createElement('div');
                    infoContainer.className = 'vehicle-card-info';

                    // Vehicle title (marca + modelo)
                    if (marker.vehicleData.marca || marker.vehicleData.modelo) {
                        const title = document.createElement('div');
                        title.className = 'vehicle-card-title';
                        const titleText = [
                            marker.vehicleData.fecha_fabricacion,
                            marker.vehicleData.marca,
                            marker.vehicleData.modelo
                        ].filter(Boolean).join(' ');
                        title.textContent = titleText;
                        infoContainer.appendChild(title);
                    }

                    // Vehicle details container
                    const detailsContainer = document.createElement('div');
                    detailsContainer.className = 'vehicle-card-details';

                    // Kilometraje
                    if (marker.vehicleData.kilometraje) {
                        const kmDetail = document.createElement('div');
                        kmDetail.className = 'vehicle-card-detail';
                        // Format number with commas
                        const formattedKm = Number(marker.vehicleData.kilometraje).toLocaleString('en-US');
                        kmDetail.textContent = `${formattedKm} km`;
                        detailsContainer.appendChild(kmDetail);
                    }

                    // Departamento
                    if (marker.vehicleData.departamento) {
                        const locationDetail = document.createElement('div');
                        locationDetail.className = 'vehicle-card-detail';
                        locationDetail.innerHTML = `<span class="vehicle-card-location-icon">📍</span> ${marker.vehicleData.departamento}`;
                        detailsContainer.appendChild(locationDetail);
                    }

                    if (detailsContainer.hasChildNodes()) {
                        infoContainer.appendChild(detailsContainer);
                    }

                    card.appendChild(infoContainer);
                }

                // Set up load handlers once on the image
                img.onload = () => {
                    // Find the placeholder that contains this card
                    const container = card?.parentElement;
                    if (container) {
                        container.style.display = 'flex';
                    }
                    setLoadedImages(prev => new Set(prev).add(marker.position));
                };

                img.onerror = () => {
                    const container = card?.parentElement;
                    if (container) {
                        container.style.display = 'none';
                    }
                };

                // Store the card element
                const newMap = new Map(renderedCards);
                newMap.set(marker.position, card);
                setRenderedCards(newMap);
            }

            // Inject the card (first time or re-inject after placeholder recreation)
            // Set visibility BEFORE appendChild to prevent flash
            if (loadedImages.has(marker.position)) {
                placeholder.style.display = 'flex';
            } else {
                placeholder.style.display = 'none';
            }

            placeholder.appendChild(card);
        });

        // Inject AlertBox elements into alert placeholders (same pattern as vehicle cards)
        parsedAnswer.alertMarkers.forEach((marker: AlertMarker) => {
            const placeholder = answerTextRef.current?.querySelector(`[data-alert-index="${marker.position}"]`) as HTMLElement;
            if (!placeholder) return;

            // Skip if this placeholder already has the alert
            if (placeholder.hasChildNodes()) {
                return;
            }

            // Check if we already have a rendered alert for this position
            let alertElement = renderedAlerts.get(marker.position);

            if (!alertElement) {
                // First time: Create new alert element
                alertElement = createAlertElement(marker);

                // Store the alert element
                const newMap = new Map(renderedAlerts);
                newMap.set(marker.position, alertElement);
                setRenderedAlerts(newMap);
            }

            // Inject the alert (first time or re-inject after placeholder recreation)
            placeholder.appendChild(alertElement);
        });

        // Attach click handler to panel link buttons
        if (onShowPanelClick) {
            const panelLinkButtons = answerTextRef.current?.querySelectorAll('[data-panel-link="true"]');
            panelLinkButtons?.forEach((button) => {
                (button as HTMLButtonElement).onclick = (e) => {
                    e.preventDefault();
                    onShowPanelClick();
                };
            });
        }
    }, [sanitizedAnswerHtml, parsedAnswer.imageMarkers, parsedAnswer.alertMarkers, renderedCards, renderedAlerts, loadedImages, onShowPanelClick]);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    {SHOW_ANALYSIS_PANEL && (
                        <div>
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "Lightbulb" }}
                                title="Show thought process"
                                ariaLabel="Show thought process"
                                onClick={() => onThoughtProcessClicked()}
                                disabled={!answer.context.thoughts?.length}
                            />
                        </div>
                    )}
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div ref={answerTextRef} className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
            </Stack.Item>

            {/* References section - Temporarily commented out
            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <IconButton
                                iconProps={{ iconName: isReferencesCollapsed ? "ChevronDown" : "ChevronUp" }}
                                title={isReferencesCollapsed ? "Expand references" : "Collapse references"}
                                ariaLabel={isReferencesCollapsed ? "Expand references" : "Collapse references"}
                                onClick={() => setIsReferencesCollapsed(!isReferencesCollapsed)}
                            />
                            <span className={styles.citationLearnMore}>References:</span>
                        </Stack>
                    </Stack>
                    {!isReferencesCollapsed && (
                        <ol>
                            {parsedAnswer.citations.map((rowId, ind) => {
                                const citation = answer.context.data_points[rowId];
                                if (!citation) return null;
                                return (
                                    <li key={rowId}>
                                        <h4>{citation.name}</h4>
                                        <p className={styles.referenceMetadata}>Brand: {citation.brand}</p>
                                        <p className={styles.referenceMetadata}>Price: {citation.price}</p>
                                        <p>{citation.description}</p>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </Stack.Item>
            )} */}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
